/**
 * Registers push subscription with backend when user is logged in and
 * notification permission is already granted (landlord app).
 * In PWA standalone (e.g. mobile), retries with longer delays so the service worker
 * has time to be ready. Also retries when the app becomes visible again so mobile
 * gets push after a failed first load.
 */

import { useEffect, useRef } from 'react';
import {
  isPushSupported,
  getPermission,
  getStoredPushChoice,
  setStoredPushChoice,
  setPushSubscriptionFailed,
  subscribeToPush,
  sendSubscriptionToBackend,
} from '../services/pushNotificationService';

function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

const PWA_RETRY_DELAYS = [2500, 5000];

export function usePushRegistration(isAuthenticated: boolean) {
  const registered = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !isPushSupported() || getPermission() !== 'granted') {
      return;
    }
    if (getStoredPushChoice() === 'denied') return;
    if (registered.current) return;

    let cancelled = false;
    const tryRegister = async (retryIndex = 0): Promise<void> => {
      const delay = isStandalonePWA() && retryIndex > 0 ? PWA_RETRY_DELAYS[retryIndex - 1] : 0;
      if (delay) await new Promise((r) => setTimeout(r, delay));
      if (cancelled) return;
      try {
        const sub = await subscribeToPush();
        if (cancelled) return;
        if (!sub) {
          setPushSubscriptionFailed(true);
          if (isStandalonePWA() && retryIndex < PWA_RETRY_DELAYS.length) {
            await tryRegister(retryIndex + 1);
          }
          return;
        }
        await sendSubscriptionToBackend(sub);
        if (!cancelled) {
          setStoredPushChoice('granted');
          setPushSubscriptionFailed(false);
          registered.current = true;
        }
      } catch {
        setPushSubscriptionFailed(true);
        if (isStandalonePWA() && retryIndex < PWA_RETRY_DELAYS.length) {
          await tryRegister(retryIndex + 1);
        }
      }
    };

    tryRegister(0);

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (registered.current || getPermission() !== 'granted') return;
      if (!isStandalonePWA()) return;
      tryRegister(0);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated]);
}
